/**
 * SettlementSummary — post-call modal showing final amounts + tx hash.
 */

import { type ReactNode } from 'react';
import { formatTokenAmount, explorerTxUrl, getChainConfig, formatDuration } from '@tokenstreaming/shared';

interface Props {
  role: 'sender' | 'receiver';
  chainId: string;
  tokenSymbol: string;
  tokenDecimals: number;
  creatorAmount: bigint;
  resolverAmount: bigint;
  platformFee: bigint;
  affiliateFee: bigint;
  txHash: string;
  elapsedMs: number;
  totalUpdates: number;
  onDismiss: () => void;
}

export function SettlementSummary({
  role,
  chainId,
  tokenSymbol,
  tokenDecimals,
  creatorAmount,
  resolverAmount,
  platformFee,
  affiliateFee,
  txHash,
  elapsedMs,
  totalUpdates,
  onDismiss,
}: Props): ReactNode {
  const chain = getChainConfig(chainId);
  const netReceiver = resolverAmount - platformFee - affiliateFee;

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <div className="modal-header">
          <h3>Stream Settled</h3>
        </div>
        <div className="modal-body">
          {/* Summary */}
          <div className="settlement-summary">
            <div className="settlement-row settlement-primary">
              <span className="settlement-label">
                {role === 'sender' ? 'You Sent' : 'You Received'}
              </span>
              <span className="settlement-value">
                {formatTokenAmount(role === 'sender' ? resolverAmount : netReceiver, tokenDecimals)} {tokenSymbol}
              </span>
            </div>

            {/* Distribution breakdown */}
            {(platformFee > 0n || affiliateFee > 0n) && (
              <div className="settlement-breakdown">
                <div className="settlement-row">
                  <span className="settlement-label text-muted">Platform fee</span>
                  <span className="settlement-value text-muted">
                    {formatTokenAmount(platformFee, tokenDecimals)} {tokenSymbol}
                  </span>
                </div>
                {affiliateFee > 0n && (
                  <div className="settlement-row">
                    <span className="settlement-label text-muted">Referral fee</span>
                    <span className="settlement-value text-muted">
                      {formatTokenAmount(affiliateFee, tokenDecimals)} {tokenSymbol}
                    </span>
                  </div>
                )}
                <div className="settlement-row">
                  <span className="settlement-label text-muted">Net to receiver</span>
                  <span className="settlement-value text-muted">
                    {formatTokenAmount(netReceiver, tokenDecimals)} {tokenSymbol}
                  </span>
                </div>
              </div>
            )}

            <div className="settlement-row">
              <span className="settlement-label">Returned to sender</span>
              <span className="settlement-value">
                {formatTokenAmount(creatorAmount, tokenDecimals)} {tokenSymbol}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="settlement-stats">
            <div className="stat-item">
              <span className="stat-label">Duration</span>
              <span className="stat-value">{formatDuration(elapsedMs)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Updates</span>
              <span className="stat-value">{totalUpdates}</span>
            </div>
          </div>

          {/* Transaction link */}
          {txHash && (
            <a
              href={explorerTxUrl(chain.explorerUrl, txHash)}
              target="_blank"
              rel="noopener noreferrer"
              className="settlement-tx-link"
            >
              View on BscScan: {txHash.slice(0, 10)}...{txHash.slice(-8)}
            </a>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-primary btn-full" onClick={onDismiss}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
